import { FormEvent, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';

import { FormInput, OnChangePseudoEvent } from '@calm-web/design-system';

import ProfileFormWrapper from '@/components/app/profile/ProfileFormWrapper';
import { NoticeMessage } from '@/components/app/profile/ProfileFormWrapper/types';
import { useAnalytics } from '@/hooks/analytics';
import { useApi } from '@/hooks/api';
import { useUserState } from '@/hooks/store';
import { setUser } from '@/store/user/actions';
import { User } from '@/store/user/types';
import { validate } from '@/utils/ui/validator';

import messages from './messages';

const UpdateUser = () => {
	const { formatMessage } = useIntl();
	const apiRequest = useApi();
	const { logEvent } = useAnalytics();

	const user = useUserState();
	const dispatch = useDispatch();

	const [firstName, setFirstName] = useState(user?.name ?? '');
	const [firstNameError, setFirstNameError] = useState<string>();
	const [email, setEmail] = useState(user?.email ?? '');
	const [emailError, setEmailError] = useState<string>();
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [noticeMessage, setNoticeMessage] = useState<NoticeMessage>({
		isSuccess: null,
		message: null,
	});

	const isSubmittable = () => {
		if (email !== user?.email || firstName !== user?.name) {
			return Boolean(email?.length > 0 && !emailError) && Boolean(firstName?.length > 0 && !firstNameError);
		}

		return false;
	};

	const onSubmit = async (e: FormEvent): Promise<void> => {
		e.preventDefault();
		if (!isSubmittable()) return;

		logEvent({
			eventName: 'Manage Subscription : Update User : Clicked',
		});

		setNoticeMessage({
			isSuccess: null,
			message: null,
		});

		setIsSubmitting(true);

		try {
			const { data: newUserData } = await apiRequest<User>({
				method: 'POST',
				endpoint: 'me',
				body: {
					email,
					name: firstName,
				},
			});

			logEvent({
				eventName: 'Manage Subscription : Update User : Success',
			});

			setNoticeMessage({
				isSuccess: true,
				message: messages.successTooltip,
			});

			dispatch(setUser(newUserData));
		} catch (err) {
			logEvent({
				eventName: 'Manage Subscription : Update User : Error',
				eventProps: {
					error: err?.data?.error,
				},
			});

			setNoticeMessage({
				isSuccess: false,
				message: messages.errorTooltip,
			});
		} finally {
			setIsSubmitting(false);
		}
	};

	const setAndValidateName = (e: OnChangePseudoEvent) => {
		const { value } = e.currentTarget;
		setNoticeMessage({
			isSuccess: null,
			message: null,
		});

		setFirstName(value);
		const isValid = validate('name', value);
		setFirstNameError(isValid ? '' : formatMessage(messages.firstNameError));
	};

	const setAndValidateEmail = (e: OnChangePseudoEvent) => {
		const { value } = e.currentTarget;
		setNoticeMessage({
			isSuccess: null,
			message: null,
		});

		setEmail(value);

		const isValid = validate('email', value);
		setEmailError(isValid ? '' : formatMessage(messages.emailError));
	};

	return (
		<ProfileFormWrapper
			onSubmit={onSubmit}
			isSubmitting={isSubmitting}
			isSubmittable={isSubmittable}
			noticeMessage={noticeMessage}
			formId="update-user-form"
		>
			<FormInput
				name="name"
				label={formatMessage(messages.firstName)}
				value={firstName}
				onChange={setAndValidateName}
				isValid={!firstNameError}
				error={firstNameError}
				errorOnDarkBackground
			/>
			<FormInput
				name="email"
				label={formatMessage(messages.email)}
				error={emailError}
				value={email}
				onChange={setAndValidateEmail}
				isValid={!emailError}
				errorOnDarkBackground
			/>
		</ProfileFormWrapper>
	);
};

export default UpdateUser;
